// src/admin/components/Card.tsx
// Surface primitive used everywhere. Three padding modes:
//   padded (default) — uses --density-card-pad so density tweak applies
//   flush             — no body padding, for tables/lists with their own gutters
//   custom <number>   — explicit pixel padding (rare, used by KPI tiles)
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import styles from './Card.module.css';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  actions?: ReactNode;
  /** padded (default) | flush | a number for explicit padding in px. */
  pad?: 'padded' | 'flush' | number;
  /** Adds a 3px cobalt left border, used for "primary" decision cards. */
  accent?: boolean;
}

export default function Card({
  title,
  actions,
  pad = 'padded',
  accent,
  children,
  className,
  style,
  ...rest
}: Props) {
  const padCls = typeof pad === 'string' ? styles[pad] : '';
  const cls = [styles.card, padCls, accent && styles.accent, className]
    .filter(Boolean).join(' ');
  const bodyStyle: CSSProperties | undefined = typeof pad === 'number'
    ? { padding: pad } : undefined;
  return (
    <div {...rest} className={cls} style={style}>
      {(title || actions) && (
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      )}
      <div className={styles.body} style={bodyStyle}>{children}</div>
    </div>
  );
}
